import jsPDF from "jspdf";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { exportToCanvas } from "./export";

import type { AppState, UIAppState } from "../types";

export const exportToPdf = async (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: UIAppState,
  files: any,
  embedSceneJSON?: string | null,
): Promise<Blob | null> => {
  const selectedPapers = elements.filter(
    (el) =>
      appState.selectedElementIds[el.id] &&
      el.customData?.isPapel &&
      !el.isDeleted,
  );

  const elementsToExport = elements.filter((el) => {
    if (el.isDeleted) {
      return false;
    }

    if (selectedPapers.some((p) => p.id === el.id)) {
      return true;
    }

    if (el.groupIds && el.groupIds.length > 0) {
      return selectedPapers.some(
        (p) =>
          p.groupIds &&
          p.groupIds.length > 0 &&
          el.groupIds.some((id) => p.groupIds!.includes(id)),
      );
    }

    return false;
  });

  if (elementsToExport.length === 0) {
    return null;
  }

  let doc: jsPDF | null = null;

  for (let i = 0; i < selectedPapers.length; i++) {
    const papel = selectedPapers[i];

    const elementsForThisPaper = elementsToExport.filter((el) => {
      if (el.id === papel.id) {
        return true;
      }
      if (el.groupIds && el.groupIds.length > 0) {
        if (papel.groupIds && papel.groupIds.length > 0) {
          return el.groupIds.some((id) => papel.groupIds!.includes(id));
        }
      }
      return false;
    });

    // Respiro para a thread principal não travar a UI (permitindo animações/loaders rodarem)
    await new Promise((resolve) => setTimeout(resolve, 20));

    const exportScale = 3; // High resolution for print quality
    const canvas = await exportToCanvas(
      elementsForThisPaper,
      { ...appState, exportScale } as unknown as AppState,
      files,
      {
        exportBackground: true,
        exportPadding: 0,
        viewBackgroundColor: appState.viewBackgroundColor,
        exportingFrame: papel as any,
      },
    );

    const outputCanvas = canvas;
    const pageOrientation =
      outputCanvas.width >= outputCanvas.height ? "landscape" : "portrait";
    const pageFormat: [number, number] = [
      outputCanvas.width / exportScale,
      outputCanvas.height / exportScale,
    ];

    if (!doc) {
      doc = new jsPDF({
        orientation: pageOrientation,
        format: pageFormat,
        unit: "pt",
      });
    } else {
      doc.addPage(pageFormat, pageOrientation);
    }

    const logicalWidth = outputCanvas.width / exportScale;
    const logicalHeight = outputCanvas.height / exportScale;

    // Respiro antes do toDataURL (processo síncrono pesado)
    await new Promise((resolve) => setTimeout(resolve, 20));
    const dataUrl = outputCanvas.toDataURL("image/png");

    // Respiro antes de injetar no PDF (processo jsPDF pesado)
    await new Promise((resolve) => setTimeout(resolve, 20));
    doc.addImage(
      dataUrl,
      "PNG",
      0,
      0,
      logicalWidth,
      logicalHeight,
      undefined, // alias
      "FAST", // compression
    );
  }

  if (doc) {
    const pdfBlob = doc.output("blob");
    if (embedSceneJSON) {
      const payload = `\n<!-- onepaper-json-start -->\n${embedSceneJSON}\n<!-- onepaper-json-end -->\n`;
      return new Blob([pdfBlob, payload], { type: "application/pdf" });
    }
    return pdfBlob;
  }

  return null;
};
